import React from "react";
import { ColoredValue } from "@src/components/ColoredValue";
import type * as T from "@src/types";

interface Props {
  metrics: T.TDT.BacktestMetrics | null | undefined;
  onlyTrackingEnded?: boolean;
}

export const RunStatsTable: React.FC<Props> = ({ metrics, onlyTrackingEnded = false }) => {
  if (!metrics) return null;

  const winRateCalc = metrics.win_rate != null ? metrics.win_rate * 100 : 0;

  return (
    <>
      <div className="SummaryItem stats-totalTrades">
        <strong>Total Trades:</strong>
        <span>{metrics.total_positions ?? 0}</span>
      </div>
      <div className="SummaryItem stats-wins">
        <strong>Wins:</strong>
        <span>
          {(metrics.win_signals ?? 0) > 0 && (
            <span className="text-muted me-1">
              (Avg <ColoredValue value={metrics.avg_win_amount ?? 0} type="currency" decimals={3} />
              {metrics.avg_win_pct != null && (
                <>
                  {" / "}
                  <ColoredValue value={metrics.avg_win_pct} type="percentage" decimals={2} />
                </>
              )}
              )
            </span>
          )}
          <ColoredValue value={metrics.win_signals ?? 0} forceColor="win" type="flat" />
        </span>
      </div>
      <div className="SummaryItem stats-losses">
        <strong>Losses:</strong>
        <span>
          {(metrics.loss_signals ?? 0) > 0 && (
            <span className="text-muted me-1">
              (Avg{" "}
              <ColoredValue value={metrics.avg_loss_amount ?? 0} type="currency" decimals={3} />
              {metrics.avg_loss_pct != null && (
                <>
                  {" / "}
                  <ColoredValue value={metrics.avg_loss_pct} type="percentage" decimals={2} />
                </>
              )}
              )
            </span>
          )}
          <ColoredValue value={metrics.loss_signals ?? 0} forceColor="loss" type="flat" />
        </span>
      </div>
      <div className="SummaryItem stats-winRate">
        <strong>Win Rate:</strong>
        <span>
          {onlyTrackingEnded ? (
            <span className="RunInfoUnknown text-muted">Unknown</span>
          ) : (
            <ColoredValue value={winRateCalc} threshold={50} type="percentage" decimals={2} />
          )}
        </span>
      </div>
      {metrics.benchmark != null && (
        <>
          <div className="SummaryItem stats-buyHoldReturn">
            <strong className="stats-buyHoldReturn-label">B&amp;H Return:</strong>
            <span className="stats-buyHoldReturn-value">
              <ColoredValue
                value={metrics.benchmark.buy_hold_return_pct}
                type="percentage"
                decimals={2}
              />
            </span>
          </div>
          <div className="SummaryItem stats-excessReturn">
            <strong className="stats-excessReturn-label">Excess Return:</strong>
            <span className="stats-excessReturn-value">
              <ColoredValue
                value={metrics.benchmark.excess_return_pct}
                type="percentage"
                decimals={2}
              />
            </span>
          </div>
          <div className="SummaryItem stats-timeInMarket">
            <strong className="stats-timeInMarket-label">Time in Market:</strong>
            <span className="stats-timeInMarket-value">
              <ColoredValue
                value={
                  metrics.benchmark.time_in_market != null
                    ? metrics.benchmark.time_in_market * 100
                    : null
                }
                type="percentage"
                decimals={2}
              />
            </span>
          </div>
          <div className="SummaryItem stats-buyHoldDrawdown">
            <strong className="stats-buyHoldDrawdown-label">B&amp;H Max Drawdown:</strong>
            <span className="stats-buyHoldDrawdown-value">
              <ColoredValue
                value={metrics.benchmark.buy_hold_max_drawdown_pct}
                type="percentage"
                decimals={2}
              />
            </span>
          </div>
          <div className="SummaryItem stats-buyHoldSharpe">
            <strong className="stats-buyHoldSharpe-label">B&amp;H Sharpe:</strong>
            <span className="stats-buyHoldSharpe-value">
              <ColoredValue value={metrics.benchmark.buy_hold_sharpe_ratio} decimals={2} />
            </span>
          </div>
        </>
      )}
    </>
  );
};
